import { portalDraftSchema, type PortalDraft } from '@warehouse/shared';

/**
 * Portal basket persistence.
 *
 * A restaurant manager assembles an order across a shift — a phone that locks,
 * a tab that gets closed, a till that needs the browser back. The basket
 * therefore survives a reload.
 *
 * WHERE it survives is a deliberate, documented compromise: `localStorage`,
 * keyed per (user, restaurant). DATA-MODEL has no `sales_order_drafts` table and
 * the schema is frozen for this module, so a server-side draft would mean a
 * migration this agent is not allowed to write. The schema request is in the
 * module report; when the table lands, only this file changes — every caller
 * already goes through `loadDraft` / `saveDraft` / `clearDraft`.
 *
 * Anything unparseable (an older shape, a hand-edited value, a quota error) is
 * discarded rather than crashing the ordering screen.
 */

const PREFIX = 'wms.portal.draft';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export function draftKey(userId: string, restaurantId: string): string {
  return `${PREFIX}:${userId}:${restaurantId}`;
}

export function loadDraft(userId: string, restaurantId: string): PortalDraft | null {
  try {
    const raw = globalThis.localStorage?.getItem(draftKey(userId, restaurantId));
    if (!raw) return null;
    const parsed = portalDraftSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    if (Date.now() - Date.parse(parsed.data.updatedAt) > MAX_AGE_MS) {
      clearDraft(userId, restaurantId);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function saveDraft(
  userId: string,
  draft: Omit<PortalDraft, 'version' | 'updatedAt'>,
): PortalDraft | null {
  const payload: PortalDraft = {
    version: 1,
    updatedAt: new Date().toISOString(),
    ...draft,
  };
  try {
    globalThis.localStorage?.setItem(draftKey(userId, draft.restaurantId), JSON.stringify(payload));
    return payload;
  } catch {
    // Private mode / quota exceeded — ordering must still work.
    return null;
  }
}

export function clearDraft(userId: string, restaurantId: string): void {
  try {
    globalThis.localStorage?.removeItem(draftKey(userId, restaurantId));
  } catch {
    /* nothing to clean up */
  }
}
